import {
  Alert,
  Button,
  Drawer,
  Dialog,
  DialogTitle,
  LinearProgress,
  MenuItem,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_SERP_MAP_STYLE, type SerpMapStyle } from '../../../api/src/fakes/serp-appearance';
import { heatmapPalette } from '../../../api/src/fakes/serp-heatmap';
import { api } from '../api/client';
import { useSerpMapSnapshot } from './useSerpMapSnapshot';
import { SERP_MAP_REFRESH } from '../../../api/src/fakes/serp-map-snapshot.types';
import type { FakesMatrixRow, FakesResult } from '../api/types';
import type { SerpMapRow } from '../../../api/src/fakes/serp-map-summary';
import type { FakesMapPreferences, FakesMapGroup } from '../../../api/src/fakes/fakes-matrix.types';
import { SerpGroupFilter } from './SerpGroupFilter';
import { SerpQueryTile } from './SerpQueryTile';
import {
  DEFAULT_SERP_TILE_METRICS,
  type SerpTileMetric,
} from '../../../api/src/fakes/serp-tile-metrics';
import { SerpGroupLabel } from './SerpGroupLabel';
import { Box, Stack, Typography } from './mui-system';
import { fakeError } from './fakes-presentation';
import { SerpMapQueryDetail } from './SerpMapQueryDetail';
import {
  compareQueryDemand,
  insetTreemapRect,
  QUERY_TILE_LEGEND,
  treemapRects,
} from '../../../api/src/fakes/serp-treemap';
import {
  volumeLabel,
  volumeWeight,
  volumeCoverage,
  volumeCoverageLabel,
} from '../../../api/src/fakes/query-volume';
function GroupedMap({
  workspaceId,
  active = true,
  fullscreen = false,
  fullscreenNotice,
  onCloseFullscreen,
  toolbar,
  runId,
  top,
  changesOnly,
  version,
  mapStyle = DEFAULT_SERP_MAP_STYLE,
  tileMetrics = DEFAULT_SERP_TILE_METRICS,
  metricsControl,
  onSelect,
}: {
  workspaceId: string;
  active?: boolean;
  fullscreen?: boolean;
  fullscreenNotice?: ReactNode;
  onCloseFullscreen?(): void;
  toolbar?: ReactNode;
  runId?: string;
  top: number;
  changesOnly: boolean;
  version: string;
  mapStyle?: SerpMapStyle;
  tileMetrics?: SerpTileMetric[];
  metricsControl?: ReactNode;
  onSelect(result: FakesResult, row: FakesMatrixRow): void;
}) {
  const theme = useTheme(),
    p = heatmapPalette(theme.palette.mode, theme.serpColors);
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mapSize, setMapSize] = useState({ width: 1000, height: 600 });
  const mapHost = useCallback((host: HTMLDivElement | null) => {
    if (!host || !window.ResizeObserver) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setMapSize({ width, height });
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const {
    data,
    error,
    status: snapshotStatus,
    retry,
  } = useSerpMapSnapshot(
    workspaceId,
    { runId, top, ...(changesOnly ? { changes: 'changed' as const } : {}) },
    version,
    active,
  );
  const [focus, setFocus] = useState('all');
  const snapshotKey = JSON.stringify(data?.materialization);
  const [selection, setSelection] = useState<{ row: SerpMapRow; snapshotKey: string } | null>(null);
  const selected = selection && selection.snapshotKey === snapshotKey ? selection.row : null;
  const setSelected = (row: SerpMapRow | null) => setSelection(row ? { row, snapshotKey } : null);
  const [preferences, setPreferences] = useState<FakesMapPreferences | null>(null);
  const [preferenceError, setPreferenceError] = useState('');
  const [preferenceAttempt, setPreferenceAttempt] = useState(0);
  const [savingSelection, setSavingSelection] = useState(false);
  const mounted = useRef(false),
    saving = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (!active) return;
    let live = true;
    setPreferenceError('');
    api
      .fakesMapPreferences(workspaceId)
      .then((next) => {
        if (live) setPreferences(next);
      })
      .catch((e) => {
        if (live) setPreferenceError(`Не удалось загрузить выбор групп: ${fakeError(e)}`);
      });
    return () => {
      live = false;
    };
  }, [workspaceId, preferenceAttempt, active]);
  async function changeGroups(groupIds: string[] | null) {
    if (saving.current) return;
    saving.current = true;
    setSavingSelection(true);
    setPreferenceError('');
    try {
      const saved = await api.saveFakesMapPreferences(workspaceId, { groupIds });
      if (mounted.current) {
        setPreferences(saved);
        setFocus('all');
        setSelected(null);
      }
    } catch (e) {
      if (mounted.current) setPreferenceError(`Не удалось сохранить выбор групп: ${fakeError(e)}`);
    } finally {
      saving.current = false;
      if (mounted.current) setSavingSelection(false);
    }
  }
  useEffect(() => {
    setSelection(null);
  }, [workspaceId, runId, top, changesOnly, version]);

  const groups = new Map<string, FakesMapGroup & { rows: SerpMapRow[] }>(
    (data?.groups ?? []).map((group) => [group.id, { ...group, rows: [] }]),
  );
  for (const row of data?.items ?? []) groups.get(row.groupId)!.rows.push(row);
  const included = [...groups.entries()].filter(
    ([id]) => preferences && (preferences.groupIds === null || preferences.groupIds.includes(id)),
  );
  const focused = focus !== 'all' && included.some(([id]) => id === focus);
  const visible = included.filter(([id]) => !focused || id === focus);
  const coverage = volumeCoverage(
    visible.flatMap(([, group]) => group.rows.map((row) => row.volume)),
  );
  const weight = (row: SerpMapRow) => volumeWeight(row.volume);
  const google = visible.some(([, group]) =>
    group.rows.some((row) => row.volume?.source === 'google_ads'),
  );
  const weighted = visible.some(([, g]) => g.rows.some((row) => weight(row) > 0));
  const withoutWeight = weighted
    ? visible.flatMap(([, g]) => g.rows.filter((row) => weight(row) <= 0))
    : [];
  const entries = visible
    .map(
      ([id, g]) =>
        [
          id,
          {
            ...g,
            rows: (weighted ? g.rows.filter((row) => weight(row) > 0) : [...g.rows]).sort(
              compareQueryDemand,
            ),
          },
        ] as const,
    )
    .filter(([, g]) => g.rows.length > 0)
    .sort(
      ([, a], [, b]) =>
        (weighted
          ? b.rows.reduce((n, row) => n + weight(row), 0) -
            a.rows.reduce((n, row) => n + weight(row), 0)
          : b.rows.length - a.rows.length) || a.name.localeCompare(b.name, 'ru'),
    );
  const groupWeight = (rows: SerpMapRow[]) => rows.reduce((sum, row) => sum + weight(row), 0);
  const boxes = treemapRects(
    entries.map(([, g]) => (weighted ? groupWeight(g.rows) : g.rows.length)),
    0,
    0,
    mapSize.width,
    mapSize.height,
  );
  const content = (
    <Box
      component="section"
      aria-label="Плитки по группам"
      sx={{
        m: fullscreen ? 0 : 2,
        p: fullscreen ? 1 : 2,
        bgcolor: p.background,
        border: fullscreen ? 0 : `1px solid ${p.border}`,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        flex: fullscreen ? 1 : undefined,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
        mb={1}
        sx={{ flexShrink: 0 }}
      >
        <Box>
          <Typography fontWeight={650}>
            Карта запросов ·{' '}
            {focused
              ? groups.get(focus)!.name
              : preferences?.groupIds === null
                ? 'все группы'
                : 'выбранные группы'}
          </Typography>

          {data && preferences && (
            <Typography fontSize={12} color="text.secondary">
              {coverage.total} запросов · Top-{data.top} ·{' '}
              {weighted ? 'площадь по частотности' : 'нет положительных весов: равные плитки'}
            </Typography>
          )}
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          {toolbar}
          {metricsControl}
          <SerpGroupFilter
            groups={data?.groups ?? []}
            value={preferences?.groupIds ?? null}
            disabled={!data || !preferences}
            saving={savingSelection}
            error={preferenceError}
            onChange={changeGroups}
          />
          <TextField
            select
            size="small"
            label="Приблизить группу"
            value={focused ? focus : 'all'}
            onChange={(e) => setFocus(e.target.value)}
            disabled={!preferences}
            sx={{ minWidth: 180, maxWidth: '100%' }}
          >
            <MenuItem value="all">
              {preferences?.groupIds === null ? 'Все группы' : 'Выбранные группы'}
            </MenuItem>
            {included.map(([id, g]) => (
              <MenuItem key={id} value={id}>
                <SerpGroupLabel name={`${g.name} · ${g.rows.length}`} profile={g.profile} />
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>
      {preferenceError && (
        <Alert
          severity="error"
          action={
            !preferences ? (
              <Button onClick={() => setPreferenceAttempt((n) => n + 1)}>
                Повторить загрузку выбора
              </Button>
            ) : undefined
          }
        >
          {preferenceError}
        </Alert>
      )}
      {!preferences && !preferenceError && <LinearProgress aria-label="Загрузка выбора групп" />}
      {error && (
        <Alert severity="error" action={<Button onClick={retry}>Повторить</Button>}>
          {error}
          {data && ' Показан последний сохранённый снимок.'}
        </Alert>
      )}
      {data && (
        <Typography fontSize={12} color="text.secondary" role="status">
          Снимок карты от{' '}
          {new Date(data.materialization?.builtAt ?? data.classifiedAt).toLocaleString('ru-RU')}.{' '}
          Даты сбора выдачи указаны внутри запросов.
        </Typography>
      )}
      {snapshotStatus?.outdated && (
        <Alert severity={snapshotStatus.error ? 'warning' : 'info'}>
          {snapshotStatus.error
            ? `Обновление не удалось: ${snapshotStatus.error}${data ? ' Показан последний сохранённый снимок.' : ' Подготовка будет повторена автоматически.'}`
            : `${data ? 'Карта обновляется в фоне. Показан сохранённый снимок.' : 'Готовим первый снимок карты.'} Группы: ${snapshotStatus.completedGroups} из ${snapshotStatus.totalGroups}.`}
          {data &&
            Date.now() - Date.parse(data.materialization?.builtAt ?? data.classifiedAt) >
              SERP_MAP_REFRESH.targetLagMs &&
            ' Снимок старше пяти минут.'}
        </Alert>
      )}
      {!data && !error && <LinearProgress aria-label="Загрузка плиток" />}
      {data && preferences && !entries.length && (
        <Typography>
          {preferences.groupIds?.length === 0
            ? 'Группы не выбраны. Выберите группы для отображения.'
            : 'Нет запросов в выбранном срезе.'}
        </Typography>
      )}
      {!!entries.length && (
        <Box
          ref={mapHost}
          data-serp-map-canvas
          sx={{
            position: 'relative',
            height: { xs: 'auto', md: fullscreen ? undefined : 600 },
            flex: { md: fullscreen ? '1 0 240px' : undefined },
            minHeight: { md: fullscreen ? 240 : undefined },
          }}
        >
          {entries.map(([id, g], i) => {
            const box = insetTreemapRect(boxes[i], 4);
            const innerHeight = Math.max(0, box.height - 32);
            const tiles = treemapRects(
              g.rows.map((row) => (weighted ? weight(row) : 1)),
              0,
              0,
              box.width,
              innerHeight,
            );
            return (
              <Box
                key={id}
                sx={{
                  position: { xs: 'relative', md: 'absolute' },
                  left: { md: box.x },
                  top: { md: box.y },
                  width: { md: `${box.width}px` },
                  height: { md: `${box.height}px` },
                  minWidth: 0,
                  minHeight: 0,
                  overflow: 'hidden',
                  mb: { xs: 1, md: 0 },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Button
                  size="small"
                  onClick={() => setFocus(focused ? 'all' : id)}
                  sx={{
                    color: p.text,
                    minHeight: 28,
                    height: 28,
                    minWidth: 0,
                    justifyContent: 'flex-start',
                    px: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    display: 'flex',
                    textOverflow: 'ellipsis',
                    flexShrink: 0,
                    textAlign: 'left',
                  }}
                >
                  <SerpGroupLabel
                    profile={g.profile}
                    name={`${g.name} · ${weighted ? `${groupWeight(g.rows).toLocaleString('ru-RU', { maximumFractionDigits: 1 })}${google ? ' / мес.' : ''} · ` : ''}${g.rows.length} ${focused ? '↩' : '›'}`}
                  />
                </Button>
                {mobile && !focused ? (
                  <Typography fontSize={12} sx={{ p: 1, color: p.muted }}>
                    Нажмите название группы, чтобы открыть запросы.
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      position: 'relative',
                      display: { xs: 'grid', md: 'block' },
                      gridTemplateColumns: 'repeat(auto-fit,minmax(125px,1fr))',
                      gridAutoRows: 'minmax(72px,auto)',
                      gap: '3px',
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    {g.rows.map((row, index) => (
                      <SerpQueryTile
                        key={row.queryId}
                        row={row}
                        rect={insetTreemapRect(tiles[index])}
                        top={data?.top ?? top}
                        mapStyle={mapStyle}
                        palette={p}
                        metrics={tileMetrics}
                        mobile={mobile}
                        onSelect={() => setSelected(row)}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      <Stack direction="row" flexWrap="wrap" gap={1.5} mt={1} sx={{ flexShrink: 0 }}>
        {QUERY_TILE_LEGEND.map(([tone, label]) => (
          <Stack key={tone} direction="row" gap={0.5} alignItems="center">
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: p[tone],
                borderRadius: 0.5,
              }}
            />
            <Typography fontSize={11} color="text.secondary">
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {data && preferences && (
        <Typography fontSize={11} color="text.secondary" sx={{ mt: 0.5, flexShrink: 0 }}>
          {volumeCoverageLabel(coverage)}
        </Typography>
      )}
      <Box component="details" sx={{ mt: 0.5, fontSize: 12, flexShrink: 0 }}>
        <Box component="summary" sx={{ cursor: 'pointer', color: 'text.secondary' }}>
          Как читать карту
        </Box>
        <Typography fontSize={12} color="text.secondary">
          Одна плитка — запрос.{' '}
          {weighted
            ? google
              ? 'Площадь — средняя частотность Google за месяц (12 месяцев); общий вес близких вариантов делится поровну между запросами, площадь группы — сумма долей.'
              : 'Площадь — точная частотность Яндекса (Wordstat) за 30 дней. Общий вес перестановок слов делится поровну; площадь группы — сумма известных долей.'
            : 'Положительных весов нет: временно площадь по числу запросов, плитки равны.'}{' '}
          Это оценка спроса, не число уникальных поисков. Зелёный цвет показывает наши позиции, а не
          полную проверку выдачи. На телефоне — список с подписями весов.{' '}
          {data?.run ? `Сбор: ${new Date(data.run.createdAt).toLocaleString('ru-RU')}.` : ''}
        </Typography>{' '}
      </Box>
      {withoutWeight.length > 0 && (
        <Box component="details" mt={1} sx={{ flexShrink: 0 }}>
          <Box component="summary" sx={{ fontSize: 13, cursor: 'pointer' }}>
            Без площади: {withoutWeight.length} запросов с нулевым или неопределённым весом
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
            {withoutWeight.map((row) => (
              <Button key={row.queryId} variant="outlined" onClick={() => setSelected(row)}>
                {row.groupName} · {row.query} · {volumeLabel(row.volume)} · {row.summary.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
      <Drawer
        anchor="right"
        open={!!selected}
        onClose={() => setSelected(null)}
        slotProps={{
          paper: {
            sx: { width: { xs: '100%', md: 680 }, maxWidth: '100%' },
            role: 'dialog',
            'aria-label': selected?.query ?? 'Запрос',
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} p={2}>
          <Typography variant="h3" sx={{ overflowWrap: 'anywhere' }}>
            {selected?.query}
          </Typography>
          <Button onClick={() => setSelected(null)}>Закрыть</Button>
        </Stack>
        <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
          {selected && (
            <>
              <Typography fontSize={13}>
                {selected.groupName} · {selected.summary.label}
              </Typography>
              {data?.materialization ? (
                <SerpMapQueryDetail
                  key={`${selected.queryId}:${data.materialization.revision}:${data.materialization.builtAt}`}
                  workspaceId={workspaceId}
                  queryId={selected.queryId}
                  input={{
                    top: data.top,
                    runId,
                    ...(changesOnly ? { changes: 'changed' as const } : {}),
                    groupId: selected.groupId,
                    ...data.materialization,
                  }}
                  onRefresh={() => {
                    setSelected(null);
                    retry();
                  }}
                  onSelect={(result, row) => {
                    setSelected(null);
                    onSelect(result, row);
                  }}
                />
              ) : (
                <Alert severity="error">Версия снимка недоступна. Обновите карту.</Alert>
              )}
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
  if (!active) return null;
  if (!fullscreen) return content;
  return (
    <Dialog
      open
      fullScreen
      onClose={onCloseFullscreen}
      aria-labelledby="serp-fullscreen-title"
      // Keep the query and resource drawers above this fullscreen surface.
      sx={{ zIndex: (theme) => theme.zIndex.drawer }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{
          px: { xs: 1.5, md: 2 },
          py: 1,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <DialogTitle id="serp-fullscreen-title" sx={{ p: 0, fontSize: 18 }}>
            Карта выдачи · плитки по группам
          </DialogTitle>
          {runId && (
            <Typography fontSize={12} color="text.secondary">
              Исторический запуск
            </Typography>
          )}
        </Box>
        <Button onClick={onCloseFullscreen}>Свернуть карту</Button>
      </Stack>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          p: { xs: 0.5, md: 1 },
        }}
      >
        {fullscreenNotice}
        {content}
      </Box>
    </Dialog>
  );
}

/** Workspace changes discard in-flight UI state before rendering the next workspace. */
export function SerpGroupedMap(props: Parameters<typeof GroupedMap>[0]) {
  return <GroupedMap key={props.workspaceId} {...props} />;
}
